import { QueryClient } from '@tanstack/react-query';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { del, get, set } from 'idb-keyval';
import { ApiError } from './client.js';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 24 * 60 * 60 * 1000,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

/**
 * The last channels, the sidebar and My work are kept in IndexedDB so the app opens instantly
 * (even offline) and channel switches render from cache before the network answers (brief:
 * Performance engineering). The cache is per browser profile and is cleared on sign-out.
 */
const PERSISTED = new Set(['sidebar', 'channel', 'messages', 'glance', 'myWork', 'me', 'statuses']);
const MAX_CHANNELS = 20;

export const persister = createAsyncStoragePersister({
  storage: {
    getItem: async (key) => (await get<string>(key)) ?? null,
    setItem: (key, value) => set(key, value),
    removeItem: (key) => del(key),
  },
  key: 'chapsi-cache-v1',
  throttleTime: 1500,
});

export function shouldPersist(queryKey: readonly unknown[]): boolean {
  return typeof queryKey[0] === 'string' && PERSISTED.has(queryKey[0]);
}

/** Keeps only the most recently used channels' messages in the persisted cache. */
export function pruneMessageCache(): void {
  const cache = queryClient.getQueryCache();
  const msgs = cache.findAll({ queryKey: ['messages'] }).sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
  for (const q of msgs.slice(MAX_CHANNELS)) cache.remove(q);
}

export async function clearLocalCache(): Promise<void> {
  queryClient.clear();
  await del('chapsi-cache-v1');
}
